import { PasswordInput } from '@/components/PasswordInput'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { isApiError } from '@/utils/error'
import { showError } from '@/utils/toast'
import { zodResolver } from '@hookform/resolvers/zod'
import { useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router-dom'
import { z } from 'zod'
import { useAuth } from '../hooks/useAuth'
import { getSafeReturnTo } from '../return-url'

type SignInForm = {
  username: string
  password: string
}

export function SignInView() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const { signin, isLoading } = useAuth()
  const signInSchema = useMemo(
    () =>
      z.object({
        username: z.string().min(1, t('auth.usernameRequired')),
        password: z.string().min(1, t('auth.passwordRequired')),
      }),
    [t],
  )

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<SignInForm>({
    resolver: zodResolver(signInSchema),
  })

  const onSubmit = async (data: SignInForm) => {
    try {
      const { userData } = await signin(data)
      navigate(userData.isPasswordChanged === false ? '/change-password' : getSafeReturnTo(location.search), {
        replace: true,
      })
    } catch (error) {
      if (isApiError(error)) {
        showError(error.message)
      }
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4 py-12">
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-2 text-center">
          <div className="mx-auto flex">
            <img src="/resources/logo.png" alt="MTrack Logo" width={96} height={96} className="object-contain" />
          </div>
          <CardTitle className="text-2xl font-bold">{t('auth.signIn')}</CardTitle>
          <CardDescription>{t('auth.description')}</CardDescription>
        </CardHeader>
        <CardContent>
          <form data-testid="signin-form" onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="username">{t('common.username')}</Label>
              <Input
                id="username"
                data-testid="signin-username-input"
                autoComplete="username"
                {...register('username')}
                placeholder={t('auth.usernamePlaceholder')}
                className={errors.username ? 'border-red-500' : ''}
              />
              {errors.username && <p data-testid="form-field-error" className="text-sm text-red-500">{errors.username.message}</p>}
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">{t('common.password')}</Label>
              <PasswordInput
                id="password"
                data-testid="signin-password-input"
                autoComplete="current-password"
                {...register('password')}
                placeholder={t('auth.passwordPlaceholder')}
                className={errors.password ? 'border-red-500' : ''}
              />
              {errors.password && <p data-testid="form-field-error" className="text-sm text-red-500">{errors.password.message}</p>}
            </div>
            <Button type="submit" data-testid="signin-submit-button" className="w-full" size="lg" disabled={isLoading}>
              {isLoading ? t('auth.signingIn') : t('auth.signIn')}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  )
}
